import { useMutation, useQuery } from '@tanstack/react-query';

import competencyProfileService, {
    type CompetencyProfileDeletionImpact,
    type CompetencyProfilePayload,
} from './competencyProfileService';

import type { PaginatedResponse } from '@/types/api';
import type {
    CompetencyProfile,
    CompetencyProfileGroup,
} from '@/types/competency/CompetencyProfile';

import { queryClient } from '@/AppWithProviders';
import {
    CompetencyProfileQueryKey,
    CompetencyQueryKey,
    ProfileAssignmentQueryKey,
} from '@/constants/QueryKeys';

const invalidateCompetencyProfiles = () => {
    queryClient.invalidateQueries({
        queryKey: [CompetencyProfileQueryKey.COMPETENCY_PROFILES],
    });
    // A profile change can alter how many profiles reference a competency, so the
    // competency cards' "Assigned to N profiles" count must refresh too.
    queryClient.invalidateQueries({
        queryKey: [CompetencyQueryKey.COMPETENCY_GROUPS],
    });

    queryClient.invalidateQueries({
        queryKey: [ProfileAssignmentQueryKey.STATISTICS],
    });
};

const useGetCompetencyProfiles = (enabled = true, competencyId?: string, groupId?: string) => {
    return useQuery<CompetencyProfile[]>({
        queryKey: [CompetencyProfileQueryKey.COMPETENCY_PROFILES, competencyId, groupId],
        queryFn: () =>
            competencyProfileService.getCompetencyProfiles({
                competencyId,
                groupId,
            }),
        enabled,
    });
};

const useCreateCompetencyProfile = () => {
    return useMutation({
        mutationFn: (payload: CompetencyProfilePayload) =>
            competencyProfileService.createCompetencyProfile(payload),
        onSuccess: invalidateCompetencyProfiles,
    });
};

const useUpdateCompetencyProfile = () => {
    return useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: CompetencyProfilePayload }) =>
            competencyProfileService.updateCompetencyProfile(id, payload),
        onSuccess: invalidateCompetencyProfiles,
    });
};

const useDeleteCompetencyProfile = () => {
    return useMutation({
        mutationFn: (id: string) => competencyProfileService.deleteCompetencyProfile(id),
        onSuccess: invalidateCompetencyProfiles,
    });
};

const useGetCompetencyProfileDeletionImpact = (id: string, enabled = true) => {
    return useQuery<CompetencyProfileDeletionImpact>({
        queryKey: [CompetencyProfileQueryKey.COMPETENCY_PROFILES, id, 'deletion-impact'],
        queryFn: () => competencyProfileService.getCompetencyProfileDeletionImpact(id),
        enabled,
    });
};

const useRemoveCompetencyProfile = () => {
    return useMutation({
        mutationFn: ({
            id,
            replacementProfileId,
        }: {
            id: string;
            replacementProfileId: string | null;
        }) => competencyProfileService.removeCompetencyProfile(id, replacementProfileId),
        onSuccess: invalidateCompetencyProfiles,
    });
};

const useSetCompetencyProfileStatus = () => {
    return useMutation({
        mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
            competencyProfileService.setCompetencyProfileStatus(id, isActive),
        onSuccess: invalidateCompetencyProfiles,
    });
};

/**
 * Fetches a competency profile with its competencies grouped by competency group.
 * Used by the admin ProfileSelector component to avoid client-side catalog lookups.
 * When employeeId is provided, includes the employee's current proficiency level for each competency.
 */
const useGetCompetencyProfileWithGroups = ({
    id,
    page = 1,
    pageSize = 50,
    enabled = true,
    employeeId,
}: {
    id: string | null;
    page?: number;
    pageSize?: number;
    enabled?: boolean;
    employeeId?: string;
}) => {
    return useQuery<PaginatedResponse<CompetencyProfileGroup>>({
        queryKey: [
            CompetencyProfileQueryKey.COMPETENCY_PROFILES,
            id,
            'groups',
            page,
            pageSize,
            employeeId,
        ],
        queryFn: () =>
            competencyProfileService.getCompetencyProfileWithGroups(id!, {
                page,
                pageSize,
                employeeId,
            }),
        enabled: enabled && !!id,
    });
};

export {
    useGetCompetencyProfiles,
    useCreateCompetencyProfile,
    useUpdateCompetencyProfile,
    useDeleteCompetencyProfile,
    useGetCompetencyProfileDeletionImpact,
    useRemoveCompetencyProfile,
    useSetCompetencyProfileStatus,
    useGetCompetencyProfileWithGroups,
};
